import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setUserData } from "../../redux/actions/userDataActions";
import Breadcrumb from "../components/Breadcrumb";
import { Button } from "../components/Form/Button";
import { Input } from "../components/Form/Input";
import AuthProvider from "../services/auth/AuthProvider/Index";
import { FormErrorsSchema } from "../types";
import { mergeErrors } from "../utils";
import notifications from "../services/notifications";

const EmailChange = () => {
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(false);
  const [currentEmail, setCurrentEmail] = useState("");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const dispatchSetUserData = (value: any) => {
        dispatch(setUserData(value));
      };

      setLoading(true);
      const identity = await AuthProvider.getIdentity();

      if (identity) {
        dispatchSetUserData(identity);
        setCurrentEmail(identity.email);
        setLoading(false);
      }
    })();
  }, [dispatch]);

  return (
    <div className="w-full lg:w-2/3 min-h-screen bg-gray-100 text-gray-900">
      <main className="mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <Breadcrumb
          pages={[
            { name: "My account", href: "#", current: false },
            { name: "Email change", href: "#", current: true },
          ]}
        />
        <div className="mt-8">
          <h1 className="text-xl font-semibold">Email change</h1>
        </div>
        <div className="bg-white mt-6 px-6 pb-6 pt-2">
          <div className="w-full">
            <div className="w-full mt-6">
              <Input
                label="Current email address"
                name="currentEmail"
                value={currentEmail}
                required={true}
                disabled={true}
              />
            </div>
            <div className="w-full mt-6">
              <Input
                label="New email address"
                name="email"
                placeholder=""
                errors={formErrors}
                value={email}
                setValue={setEmail}
                disabled={loading}
                required={true}
                inputMask={(inputText: string) => {
                  return inputText.replaceAll(/[^a-zA-Z0-9.\-_@]/g, "");
                }}
              />
            </div>
            {requested && (
              <div className="w-full mt-6">
                <Input
                  label="Confirmation token"
                  name="token"
                  placeholder=""
                  errors={formErrors}
                  value={token}
                  setValue={setToken}
                  disabled={loading}
                  required={true}
                  inputMask={(inputText: string) => {
                    return inputText.replaceAll(/[^a-zA-Z0-9]/g, "");
                  }}
                />
              </div>
            )}
          </div>
          <div className="w-full mt-10">
            {!requested && (
              <Button
                label="Request email change"
                disabled={loading}
                theme="primary"
                size="medium"
                className="block w-full md:ml-auto md:w-auto"
                onClick={async () => {
                  try {
                    setLoading(true);
                    setFormErrors({});

                    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/identity/me/email-change-request`;

                    const response = await fetch(apiUrl, {
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
                      },
                      method: "POST",
                      body: JSON.stringify({
                        email: email,
                      }),
                    });

                    if (!response.ok) {
                      if (response.status === 422) {
                        const errorData = await response.json();
                        setFormErrors(mergeErrors(errorData.errors));
                      } else {
                        throw new Error("Failed to load resource");
                      }
                    }

                    if (response.ok) {
                      notifications.success({message: "Your email change request has been successfully processed. Please take a moment to check your new email for the confirmation token."})

                      setRequested(true);
                    }
                  } catch (error) {
                    console.error("Error fetching data:", error);

                    notifications.error({message: "Something went wrong while trying to request the email change."});
                  } finally {
                    setLoading(false);
                  }
                }}
              />
            )}

            {requested && (
              <Button
                label="Confirm email change"
                disabled={loading}
                theme="primary"
                size="medium"
                className="block w-full md:ml-auto md:w-auto"
                onClick={async () => {
                  try {
                    setLoading(true);
                    setFormErrors({});

                    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/identity/me/email`;

                    const response = await fetch(apiUrl, {
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
                      },
                      method: "PATCH",
                      body: JSON.stringify({
                        email: email,
                        token: token,
                      }),
                    });

                    if (!response.ok) {
                      if (response.status === 422) {
                        const errorData = await response.json();
                        setFormErrors(mergeErrors(errorData.errors));
                      } else {
                        throw new Error("Failed to load resource");
                      }
                    }

                    if (response.ok) {
                      const identity = await AuthProvider.getIdentity();

                      if (identity) {
                        const dispatchSetUserData = (value: any) => {
                          dispatch(setUserData(value));
                        };
                        dispatchSetUserData(identity);
                        setCurrentEmail(identity.email);
                      }

                      notifications.success({message: "Your email has been successfully changed."})

                      setRequested(false);
                      setEmail("");
                      setToken("");
                    }
                  } catch (error) {
                    console.error("Error fetching data:", error);

                    notifications.error({message: "Something went wrong while trying to change your email."})
                  } finally {
                    setLoading(false);
                  }
                }}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmailChange;
